// composables/paginateAll.ts
// ---------------------------------------------------------------------------
// Récupère TOUS les items d'une requête Directus en paginant par 1000.
//
// Directus applique QUERY_LIMIT_MAX = 1000 : non seulement un `limit` entier
// > 1000 renvoie une erreur 400, mais `limit=-1` (« illimité ») est LUI AUSSI
// plafonné à 1000 → troncature silencieuse. La seule façon fiable de tout
// récupérer est de boucler page par page jusqu'à une page incomplète.
//
// `fetchPage(page, limit)` doit renvoyer le tableau d'items de la page demandée.
// (Version app / client — jumelle de server/utils/paginateAll.ts.)
// ---------------------------------------------------------------------------
export async function paginateAll<T>(
  fetchPage: (page: number, limit: number) => Promise<T[] | null | undefined>,
  pageSize = 1000,
  maxRetries = 3,
): Promise<T[]> {
  const all: T[] = []
  let page = 1
  while (page <= 100) {
    let batch: T[] | null | undefined
    let lastErr: unknown
    // Directus renvoie parfois un 503 transitoire (surcharge) sur ces requêtes :
    // on réessaie quelques fois avant d'abandonner.
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        batch = await fetchPage(page, pageSize)
        lastErr = undefined
        break
      } catch (e) {
        lastErr = e
        if (attempt < maxRetries) {
          await new Promise((r) => setTimeout(r, 500 * attempt))
        }
      }
    }
    if (lastErr) {
      // Échec définitif : on garde ce qu'on a plutôt que de planter la page.
      console.warn(`[paginateAll] page ${page} en échec après ${maxRetries} essais, arrêt à ${all.length} items`, lastErr)
      break
    }
    if (!batch || batch.length === 0) break
    all.push(...batch)
    if (batch.length < pageSize) break
    page++
  }
  return all
}
